import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Container, Row, Col, Card, Modal } from 'react-bootstrap';
import { useAuth } from '../AuthContext';
import { useTheme } from '../../ThemeContext';
import BottomNav from '../BottomNav';
import Codee from '../ui/Codee';
import { useNavigate } from 'react-router-dom';

const SubscriptionForm = () => {
  const { userInfo, isLoading, isAuthenticated, payWithBonus, payWithRegular } = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [balance, setBalance] = useState(0);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentLink, setPaymentLink] = useState('');
  const [paymentAmount, setPaymentAmount] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoFeedback, setPromoFeedback] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const subscriptionPlans = [
    { days: 7, cost: 100 },
    { days: 30, cost: 300 },
    { days: 90, cost: 750 },
    { days: 180, cost: 1350 },
  ];
  const thirtyDayPlan = subscriptionPlans.find((plan) => plan.days === 30);
  const PRICE_PER_DAY_BONUS = 5;
  const bonusDayOptions = [1, 5, 10, 15, 30];

  useEffect(() => {
    const initializeAuth = async () => {
      if (isAuthenticated && !isLoading) {
        setBalance(userInfo.balance);
        if (userInfo.email) {
          setEmail(userInfo.email);
        }
      }
    };
    initializeAuth();
  }, [userInfo, isLoading, isAuthenticated]);

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setPromoFeedback(null);
    setPaymentAmount(null);
    if (plan.days !== 30) {
      setPromoCode('');
    }
    setShowModal(true);
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);

    if (!emailPattern.test(newEmail)) {
      setEmailError('Неверный формат email');
    } else {
      setEmailError('');
    }
  };

  const handleBonusPayment = async (days) => {
    try {
      setLoading(true);
      setError('');

      const data = await payWithBonus(days); // <-- передаём дни

      if (data?.status) {
        // Обновим баланс и сообщим срок
        if (typeof data.balance === 'number') setBalance(data.balance);
        if (data.until) alert(`Подписка продлена до ${data.until} (МСК).`);
        navigate('/');
      } else {
        // сервер вернёт detail и текущий баланс
        const msg = data?.detail || 'Не хватает денег на балансе';
        const b = typeof data?.balance === 'number' ? ` (баланс: ${data.balance}₽)` : '';
        setError(`${msg}${b}`);
      }
    } catch (err) {
      setError(err.message || 'Ошибка при оплате бонусами');
    } finally {
      setLoading(false);
    }
  };

  const handleRegularPayment = async () => {
    try {
      setError('');
      setPromoFeedback(null);
      setLoading(true);

      // Проверяем валидность email и наличие выбранного плана
      if (!emailPattern.test(email)) {
        throw new Error('Неверный формат email');
      }
      if (!selectedPlan) {
        throw new Error('План не выбран');
      }

      const data = await payWithRegular(
        email,
        selectedPlan.days,
        selectedPlan.cost,
        promoCode
      );

      if (!data?.paymentLink) {
        throw new Error('Не удалось получить платежную ссылку');
      }

      if (data?.promo) {
        setPromoFeedback(data.promo);
      }

      // Сначала обновляем состояние, потом закрываем модалку
      setPaymentLink(data.paymentLink);
      if (typeof data?.amount === 'number') {
        setPaymentAmount(data.amount);
      } else {
        setPaymentAmount(selectedPlan.cost);
      }
      setShowModal(false);

    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Произошла ошибка при оплате');
      setShowModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handlePromoQuickApply = async () => {
    try {
      setError('');
      setPromoFeedback(null);
      setLoading(true);

      const normalizedPromo = (promoCode || '').trim().toUpperCase();
      if (!normalizedPromo) {
        throw new Error('Введите промокод');
      }
      const accountEmail = (userInfo?.email || email || '').trim();
      if (!emailPattern.test(accountEmail)) {
        throw new Error('Для применения промокода сначала подтвердите email в настройках профиля');
      }
      if (!thirtyDayPlan) {
        throw new Error('Тариф 30 дней недоступен');
      }

      const data = await payWithRegular(
        accountEmail,
        thirtyDayPlan.days,
        thirtyDayPlan.cost,
        normalizedPromo
      );

      if (!data?.promo?.applied) {
        setPaymentLink('');
        setPaymentAmount(null);
        setSelectedPlan('');
        setPromoFeedback(data?.promo || {
          applied: false,
          message: 'Промокод не применился. Проверьте условия и попробуйте еще раз.',
        });
        return;
      }

      if (!data?.paymentLink) {
        throw new Error('Не удалось получить платежную ссылку');
      }

      setPromoCode(normalizedPromo);
      setPromoFeedback(data.promo);
      setSelectedPlan({
        days: Number(data?.days) || thirtyDayPlan.days,
        cost: thirtyDayPlan.cost,
      });
      setPaymentLink(data.paymentLink);
      setPaymentAmount(
        typeof data?.amount === 'number' ? data.amount : thirtyDayPlan.cost
      );
    } catch (err) {
      setPaymentLink('');
      setPaymentAmount(null);
      setSelectedPlan('');
      setError(err.message || 'Ошибка при применении промокода');
    } finally {
      setLoading(false);
    }
  };

  const themeClass = theme === 'dark' ? 'text-white bg-dark' : 'text-dark bg-light';
  const headerStyle = {
    borderRadius: '10px',
    padding: '20px',
    margin: '10px',
    backgroundColor: theme === 'dark' ? '#212529' : '#f8f9fa',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
  };

  const renderPromoQuickBlock = () => {
    const accountEmail = (userInfo?.email || email || '').trim();
    const hasConfirmedEmail = emailPattern.test(accountEmail);

    return (
      <div
        className={`w-100 mt-3 mb-3 p-3 rounded border ${themeClass}`}
        style={{ backgroundColor: theme === 'dark' ? '#1f242b' : '#ffffff' }}
      >
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-1 mb-2">
          <Card.Title className="mb-0">Промокод</Card.Title>
          <small style={{ opacity: 0.8 }}>Только для тарифа 30 дней</small>
        </div>

        <Card.Text className="small mb-2 text-start">
          Email берется из профиля: <strong>{hasConfirmedEmail ? accountEmail : 'не подтвержден'}</strong>
        </Card.Text>

        <div className="d-flex flex-column flex-md-row gap-2">
          <Form.Control
            type="text"
            placeholder="Введите промокод"
            className={`search-input ${theme === 'dark' ? 'bg-dark text-white' : 'bg-light text-dark'}`}
            value={promoCode}
            onChange={(e) => setPromoCode((e.target.value || '').toUpperCase())}
            disabled={loading}
            maxLength={64}
          />
          <Button
            variant="primary"
            onClick={handlePromoQuickApply}
            disabled={loading || !promoCode?.trim() || !hasConfirmedEmail}
          >
            {loading ? 'Проверка...' : 'Применить'}
          </Button>
        </div>

        {!hasConfirmedEmail && (
          <Alert variant="warning" className="mt-2 mb-0 text-start">
            Для промокода сначала привяжите и подтвердите email в настройках профиля.
          </Alert>
        )}

        {promoFeedback && !paymentLink && (
          <Alert
            variant={promoFeedback.applied ? 'success' : 'warning'}
            className="mt-2 mb-0 text-start"
          >
            {promoFeedback.message}
          </Alert>
        )}
      </div>
    );
  };

  return (
    <Container className={`pt-3 pb-5 ${themeClass} scrollable`}>
      <Row className="justify-content-md-center">
        <Col>
          <Card className={`d-flex align-items-center justify-content-center text-center ${themeClass} shadow-lg`} style={headerStyle}>
            <Card.Body>
              {paymentLink && selectedPlan ? (
                <>
                  <Card.Title>Оплата подписки на {selectedPlan.days} дней в Salew_Bot</Card.Title>
                  {promoFeedback && (
                    <Alert
                      variant={promoFeedback.applied ? 'success' : 'warning'}
                      className="mt-3"
                    >
                      {promoFeedback.message}
                    </Alert>
                  )}
                  <Alert variant="success" className="mt-3 p-4">
                    <Card.Text>
                      Нажимая на кнопку <strong>Оплатить {paymentAmount ?? selectedPlan.cost} рублей</strong>, вы соглашаетесь с&nbsp;
                      <a href="https://salew-bot.ru/agreement.pdf" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: 'blue' }}>
                        Пользовательским соглашением
                      </a>
                      &nbsp;и&nbsp;
                      <a href="https://salew-bot.ru/politika-obrabotki-personalnyh-dannyh.pdf" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: 'blue' }}>
                        Политикой обработки персональных данных
                      </a>.
                    </Card.Text>

                  </Alert>
                  <Button
                    variant="primary"
                    href={paymentLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3"
                  >
                    Оплатить {paymentAmount ?? selectedPlan.cost} рублей
                  </Button>
                </>

              ) : balance >= PRICE_PER_DAY_BONUS ? (
                <>
                  <Card.Title>Вы можете оплатить бонусами!</Card.Title>
                  <Card.Text>
                    Ваш реферальный баланс: {balance.toFixed(2)} руб.
                    <br />
                    Ваш ID: <Codee>{userInfo ? userInfo.user_id : null}</Codee>
                  </Card.Text>
                  {renderPromoQuickBlock()}

                  <div className="d-flex flex-wrap justify-content-center">
                    {bonusDayOptions.map((d) => {
                      const cost = d * PRICE_PER_DAY_BONUS;
                      const can = balance >= cost;
                      return (
                        <Button
                          key={d}
                          variant={can ? "success" : "outline-secondary"}
                          className="m-2"
                          disabled={loading || !can}
                          onClick={() => handleBonusPayment(d)}
                        >
                          {d} дн • {cost} руб.
                        </Button>
                      );
                    })}
                  </div>
                  <hr />
                  <div className="d-flex flex-wrap justify-content-center">
                    {subscriptionPlans.map((plan) => (
                      <Button
                        key={plan.days}
                        variant={plan.cost === 50 ? "outline-warning" : "outline-primary"}
                        className="m-2"
                        size={plan.cost === 50 ? 'lg' : null}
                        onClick={() => handlePlanSelect(plan)}
                        disabled={loading}
                      >
                        {plan.cost === 50 ? "Пробная подписка - " : null}{plan.days} дней - {plan.cost} руб.
                      </Button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <Card.Title>Выберите тариф</Card.Title>
                  <Card.Text>
                    Ваш ID: <Codee>{userInfo ? userInfo.user_id : null}</Codee>
                  </Card.Text>
                  {renderPromoQuickBlock()}
                  <hr />
                  <div className="d-flex flex-wrap justify-content-center">
                    {subscriptionPlans.map((plan) => (
                      <Button
                        key={plan.days}
                        variant={plan.cost === 50 ? "outline-warning" : "outline-primary"}
                        className="m-2"
                        size={plan.cost === 50 ? 'lg' : null}
                        onClick={() => handlePlanSelect(plan)}
                        disabled={loading}
                      >
                        {plan.cost === 50 ? "Пробная подписка - " : null}{plan.days} дней - {plan.cost} руб.
                      </Button>
                    ))}
                  </div>
                </>
              )}
              {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showModal} onHide={() => {
        setShowModal(false);
        setSelectedPlan('');
        setPaymentLink('');
        setPaymentAmount(null);
        setPromoFeedback(null);
        setPromoCode('');
      }}>
        <Modal.Header closeButton className={theme === 'dark' ? 'border-bottom border-secondary bg-dark text-white' : 'border-bottom border-secondary bg-light text-dark'}>
          <Modal.Title>Введите Email для оплаты</Modal.Title>
        </Modal.Header>
        <Modal.Body className={theme === 'dark' ? 'bg-dark text-white' : 'bg-light text-dark'}>
          <Form>
            <Form.Group controlId="formEmail">
              <Form.Label>Email адрес</Form.Label>
              <Form.Control
                type="email"
                inputMode="email"
                placeholder="Введите email"
                className={`search-input ${theme === 'dark' ? 'bg-dark text-white' : 'bg-light text-dark'} ${emailError ? 'is-invalid' : ''}`}
                value={email}
                onChange={handleEmailChange}
                disabled={loading}
              />
              {emailError && <div className="invalid-feedback">{emailError}</div>}
              {error && (
                <div className="mt-2 text-danger small">
                  ⚠️ {error}
                </div>
              )}
            </Form.Group>
            {selectedPlan?.days === 30 && (
              <Form.Group controlId="formPromo" className="mt-3">
                <Form.Label>Промокод (необязательно)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Например, WELCOME10"
                  className={`search-input ${theme === 'dark' ? 'bg-dark text-white' : 'bg-light text-dark'}`}
                  value={promoCode}
                  onChange={(e) => setPromoCode((e.target.value || '').toUpperCase())}
                  disabled={loading}
                  maxLength={64}
                />
                <Form.Text style={{ color: theme === 'dark' ? '#adb5bd' : '#495057' }}>
                  Промокоды действуют только на тариф 30 дней и только при подтвержденном email.
                </Form.Text>
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer className={theme === 'dark' ? 'border-top border-secondary bg-dark text-white' : 'border-top border-secondary bg-light text-dark'}>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Отмена
          </Button>
          <Button
            variant="primary"
            onClick={handleRegularPayment}
            disabled={loading || !email || emailError}
          >
            {loading ? 'Обработка...' : 'Оплатить'}
          </Button>
        </Modal.Footer>
      </Modal>
      <BottomNav />
    </Container>
  );
};

export default SubscriptionForm;
